import React, { useState } from "react";
import { Form, Input, Button, message, Upload, Select, InputNumber } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../../constants/urlConstants";

const { TextArea } = Input;
const { Option } = Select;

const AddPerfumeForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [actualPrice, setActualPrice] = useState<number | null>(null);
  const [discount, setDiscount] = useState<number | null>(null);

  const categoryMap: Record<string, string[]> = {
    "Baby & Kids": [
      "Baby Clothes",
      "Toys",
      "Baby Soaps",
      "Baby Lotion",
      "Baby Powder",
      "Newborn Essentials",
      "Strollers & Cradles"
    ],

    "Clothing & Fashion": [
      "Men's Clothing",
      "Women's Clothing",
      "Kids' Clothing",
      "Sportswear",
      "T-Shirts",
      "Sarees",
      "Dresses",
      "Ethnic Wear",
      "Winter Wear",
      "Fabrics"
    ],

    "Furniture": [
      "Living Room Furniture",
      "Bedroom Furniture",
      "Office Furniture",
      "Kitchen Furniture",
      "Tables & Chairs",
      "Cupboards",
      "Sofas",
      "Storage Solutions"
    ],

    "Kitchen & Home": [
      "Cookware",
      "Appliances",
      "Utensils",
      "Storage Containers",
      "Tableware",
      "Steel Pots",
      "Gas Stoves",
      "Chimneys",
      "Kitchen Materials",
      "Steel Kitchen Materials"
    ],

    "Industrial & Machinery": [
      "Agricultural Machinery",
      "Roti Maker Machines",
      "Roti Making Machines",
      "Milk Making Products",
      "Milk & Ghee Separator Machines",
      "Printing Machines",
      "T-Shirt Printing Machines",
      "Xerox Machines",
      "Toilet Paper Machines",
      "Flour Mill Machines",
      "Tailor Machines",
      "Paper Plate Machines",
      "Water Purification Machines",
      "Water Purifier Machines",
      "Steel Industries",
      "Fabrication Equipment",
      "Jewellery Cleaning Machines",
      "Jewellery Making Tools",
      "Polishing Machines",
      "Casting Equipment",
      "Moulding Tools"
    ],

    "Electronics & Appliances": [
      "Televisions",
      "Air Conditioners",
      "Refrigerators",
      "Fans",
      "Coolers",
      "Mixies",
      "Grinders"
    ],

    "Mobile Phones": [
      "Mobile Phones",
      "Mobile Accessories",
      "Smart Watches",
      "Headphones",
      "Laptops"
    ],

    "Automobile": [
      "Car Accessories",
      "Motorbike Gear",
      "Tyres",
      "Car Care",
      "Lubricants",
      "Vehicle Parts"
    ],

    "Beauty & Personal Care": [
      "Perfumes",
      "Makeup",
      "Skincare",
      "Haircare",
      "Nail Polish",
      "Body Lotions",
      "Beauty Tools",
      "Bangles",
      "1 Gram Jewellery"
    ],

    "Sports & Fitness": [
      "Gym Equipment",
      "Sportswear",
      "Footwear",
      "Outdoor Gear",
      "Accessories",
      "Bicycles"
    ],

    "Books & Stationery": [
      "Fiction",
      "Non-Fiction",
      "Educational",
      "Children Books",
      "Comics",
      "Office Stationery",
      "Art & Craft Supplies"
    ],

    "Apparel & Gifts": [
      "Gift Photo Frames",
      "Gift Products",
      "Brass Materials",
      "Jewellery (Gold Plated / Imitation)",
      "Bangles",
      "Trophies",
      "Trophy & Shields",
      "Decor Items",
      "Showpieces",
      "Corporate Gifts"
    ]
  };


  const mainCategories = Object.keys(categoryMap).sort();

  const handleMainCategoryChange = (value: string) => {
    const subs = categoryMap[value] ? [...categoryMap[value]].sort() : [];
    setSubCategories(subs);
    form.setFieldsValue({ subCategory: undefined });
  };

const handlePriceCalculation = (actual?: number, disc?: number) => {
  const a = actual ?? actualPrice ?? 0;
  const d = disc ?? discount ?? 0;

  if (a && d >= 0) {
    const calculated = Math.floor(a - (a * d) / 100);  // FIXED
    form.setFieldsValue({ price: calculated });
  } else {
    form.setFieldsValue({ price: a });
  }
};


  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const normalizedValues = {
        ...values,
        tags:
          typeof values.tags === "string"
            ? values.tags
                .split(",")
                .map((t: string) => t.trim().toLowerCase())
                .join(", ")
            : "",
      };

      const formData = new FormData();
      if (file) formData.append("file", file);
      formData.append("perfume", new Blob([JSON.stringify(normalizedValues)], { type: "application/json" }));

        const response = await axios.post(`${BASE_URL}/api/v1/admin/add`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });


      message.success("Product added successfully!");
      console.log("Response:", response.data);
      form.resetFields();
      setFile(null);
    } catch (error: any) {
      console.error("Backend error:", error);
      const backendError = error.response?.data;
      let errorMessage = "Unexpected error occurred.";

      if (backendError) {
        if (typeof backendError === "string") errorMessage = backendError;
        else if (backendError.error && typeof backendError.error === "string") errorMessage = backendError.error;
        else if (typeof backendError === "object") {
          try {
            const messages = Object.values(backendError).join(", ");
            errorMessage = messages || JSON.stringify(backendError);
          } catch {
            errorMessage = JSON.stringify(backendError);
          }
        }
      }
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
      style={{
        maxWidth: 700,
        margin: "30px auto",
        padding: 24,
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ textAlign: "center", marginBottom: 24 }}>Add New Product</h2>

      {/* 🏷 Product Info */}
      <Form.Item label="Product Title" name="perfumeTitle" rules={[{ required: true, message: "Please enter product title" }]}>
        <Input placeholder="Enter product title" />
      </Form.Item>

      <Form.Item label="Brand" name="brand" rules={[{ required: true, message: "Please enter brand" }]}>
        <Input placeholder="Enter brand name" />
      </Form.Item>

      {/* 🏷 Category */}
      <Form.Item label="Main Category" name="category" rules={[{ required: true, message: "Please select main category" }]}>
        <Select placeholder="Select main category" onChange={handleMainCategoryChange}>
          {mainCategories.map((cat) => (
            <Option key={cat} value={cat}>
              {cat}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Sub Category" name="subCategory" rules={[{ required: true, message: "Please select subcategory" }]}>
        <Select placeholder="Select subcategory" disabled={!subCategories.length}>
          {subCategories.map((sub) => (
            <Option key={sub} value={sub}>
              {sub}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* 🧍 Gender */}
      <Form.Item label="Gender" name="gender" rules={[{ required: true, message: "Please select gender" }]}>
        <Select placeholder="Select gender">
          <Option value="male">Male</Option>
          <Option value="female">Female</Option>
          <Option value="unisex">Others</Option>
        </Select>
      </Form.Item>

      {/* ⚙️ Type & Specifications */}
      <Form.Item label="Type" name="type">
        <Input placeholder="e.g., Spray, Roll-on" />
      </Form.Item>

      <Form.Item label="Specifications" name="specifications">
        <TextArea rows={2} placeholder="Enter specifications or product details" />
      </Form.Item>

      {/* 📦 Stock Info */}
      <Form.Item label="Quantity in Stock" name="quantity" rules={[{ required: true, message: "Please enter available stock quantity" }]}>
        <InputNumber min={1} placeholder="Enter available quantity" style={{ width: "100%" }} />
      </Form.Item>

      {/* 🧮 Minimum Order Quantity */}
      <Form.Item
        label="Minimum Order Quantity"
        name="minQuantity"
        rules={[{ required: true, message: "Please enter minimum order quantity" }]}
      >
        <InputNumber min={1} placeholder="Enter minimum order quantity" style={{ width: "100%" }} />
      </Form.Item>

      {/* 🧮 Maximum Order Quantity */}
            <Form.Item
              label="Maximum Order Quantity"
              name="maxQuantity"
              rules={[{ required: true, message: "Please enter maximum order quantity" }]}
            >
              <InputNumber min={1} placeholder="Enter maximum order quantity" style={{ width: "100%" }} />
            </Form.Item>

      {/* 🧮 Shipping Order Quantity */}
                  <Form.Item
                    label="Shipping charges Percentage"
                    name="shipPer"
                    rules={[{ required: true, message: "Please enter shipping charges percentage" }]}
                  >
                    <InputNumber min={1} placeholder="Enter shipping charges percentage" style={{ width: "100%" }} />
                  </Form.Item>


      <Form.Item label="Unit Type" name="unit" rules={[{ required: true, message: "Please enter unit type" }]}>
        <Input placeholder="e.g., kg, pcs, ml" />
      </Form.Item>

      {/* 💰 Pricing */}
      <Form.Item label="Actual Price (₹)" name="actualPrice" rules={[{ required: true, message: "Please enter actual price" }]}>
        <InputNumber
          min={0}
          placeholder="Enter actual/original price"
          style={{ width: "100%" }}
          onChange={(val) => {
            setActualPrice(val || 0);
            handlePriceCalculation(val || 0, discount || 0);
          }}
        />
      </Form.Item>

      <Form.Item label="Discount (%)" name="discount">
        <InputNumber
          min={0}
          max={100}
          placeholder="Enter discount percentage"
          style={{ width: "100%" }}
          onChange={(val) => {
            setDiscount(val || 0);
            handlePriceCalculation(actualPrice || 0, val || 0);
          }}
        />
      </Form.Item>

      <Form.Item label="Final Price (₹)" name="price" rules={[{ required: true, message: "Auto-calculated price is required" }]}>
        <InputNumber style={{ width: "100%" }} readOnly />
      </Form.Item>

      {/* 🚚 Delivery Timeline (Dropdown) */}
      <Form.Item label="Delivery Timeline" name="deliveryTimeline" rules={[{ required: true, message: "Please select delivery time" }]}>
        <Select placeholder="Select delivery timeline">
          <Option value="2 Days">2 Days</Option>
          <Option value="5 Days">5 Days</Option>
          <Option value="7 Days">7 Days</Option>
          <Option value="15 Days">15 Days</Option>
          <Option value="30 Days">30 Days</Option>
        </Select>
      </Form.Item>

      {/* 🧾 Seller Info */}
      <Form.Item label="Seller Name" name="sellerName">
        <Input placeholder="Enter seller name (optional)" />
      </Form.Item>

      {/* 📍 Status */}
      <Form.Item label="Product Status" name="status" rules={[{ required: true, message: "Please select product status" }]}>
        <Select placeholder="Select status">
          <Option value="active">Active</Option>
          <Option value="inactive">Inactive</Option>
          <Option value="draft">Draft</Option>
        </Select>
      </Form.Item>

      {/* 🏷 Tags */}
      <Form.Item label="Tags" name="tags">
        <Input placeholder="Comma-separated tags e.g. floral, citrus, casual" />
      </Form.Item>

      {/* 📝 Description */}
      <Form.Item label="Product Description" name="description" rules={[{ required: true, message: "Please enter description" }]}>
        <TextArea rows={4} placeholder="Enter detailed product description" />
      </Form.Item>

      {/* 🖼 Upload */}
      <Form.Item label="Upload Image File">
        <Upload
          beforeUpload={(file) => {
            setFile(file);
            return false;
          }}
          maxCount={1}
          accept="image/*"
        >
          <Button icon={<UploadOutlined />}>Select Image</Button>
        </Upload>
        {file && <p style={{ marginTop: 8 }}>Selected file: {file.name}</p>}
      </Form.Item>

      {/* 🔘 Submit */}
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block>
          Add Product
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AddPerfumeForm;
